import { useState, useEffect } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

interface HeatmapCell {
  day: number;   // 0=Mon … 6=Sun
  hour: number;  // 0-23
  roas: number;
  revenue: number;
  spend: number;
  count: number;
}

function roasBg(roas: number): string {
  if (roas <= 0) return '#1e293b'; // no data
  if (roas < 1.0) return '#4c0519';
  if (roas < 1.5) return '#f43f5e';
  if (roas < 2.0) return '#fb923c';
  if (roas < 2.5) return '#fbbf24';
  if (roas < 3.0) return '#86efac';
  if (roas < 3.5) return '#10b981';
  return '#059669';
}

function fmtHour(h: number) {
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

// Convert JS Date day (0=Sun) to our index (0=Mon)
function jsDayToIndex(jsDay: number): number {
  return (jsDay + 6) % 7;
}

export function Heatmap() {
  const [hover, setHover] = useState<(HeatmapCell & { mouseX: number; mouseY: number }) | null>(null);
  const [lookup, setLookup] = useState<Map<string, HeatmapCell>>(new Map());
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.roas.hourlyHeatmap.query({ tenantId: user.tenantId })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((snapshots: any[]) => {
        if (!snapshots.length) { setLoading(false); return; }

        // Accumulate by (dayOfWeek, hour) and average
        const accum = new Map<string, { roas: number[]; revenue: number[]; cost: number[] }>();

        for (const s of snapshots) {
          const dateStr: string = typeof s.date === 'string' ? s.date : new Date(s.date).toISOString().slice(0, 10);
          // Try to get hour from dimensionValue
          const hour = parseInt(s.dimensionValue ?? '');
          if (isNaN(hour) || hour < 0 || hour > 23) continue;

          const dayOfWeek = jsDayToIndex(new Date(dateStr + 'T00:00:00Z').getUTCDay());
          const key = `${dayOfWeek}-${hour}`;

          const existing = accum.get(key) ?? { roas: [], revenue: [], cost: [] };
          existing.roas.push(parseFloat(s.roas ?? '0'));
          existing.revenue.push(parseFloat(s.revenue ?? '0'));
          existing.cost.push(parseFloat(s.cost ?? '0'));
          accum.set(key, existing);
        }

        const map = new Map<string, HeatmapCell>();
        for (const [key, vals] of accum.entries()) {
          const [dayStr, hourStr] = key.split('-');
          const day = parseInt(dayStr);
          const hour = parseInt(hourStr);
          const avgRoas = vals.roas.reduce((a, b) => a + b, 0) / vals.roas.length;
          const totalRevenue = vals.revenue.reduce((a, b) => a + b, 0);
          const totalSpend = vals.cost.reduce((a, b) => a + b, 0);
          map.set(key, { day, hour, roas: avgRoas, revenue: totalRevenue, spend: totalSpend, count: vals.roas.length });
        }

        setLookup(map);
        setHasData(map.size > 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">ROAS Heatmap (Hour × Day)</h3>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-rose-500 inline-block" /> Low</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" /> Mid</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> High</span>
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="text-slate-600 text-sm animate-pulse">Loading heatmap…</div>
        </div>
      ) : !hasData ? (
        <div className="h-48 flex items-center justify-center flex-col gap-2">
          <p className="text-slate-600 text-sm">No hourly ROAS data available yet.</p>
          <p className="text-slate-700 text-xs">Data will appear as calls and spend are recorded over time.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {/* Hour labels */}
            <div className="flex ml-12 mb-1">
              {HOURS.map(h => (
                <div key={h} className="w-8 text-center text-[10px] text-slate-600 leading-none">
                  {h % 4 === 0 ? fmtHour(h) : ''}
                </div>
              ))}
            </div>

            {/* Grid rows */}
            {DAYS.map((day, di) => (
              <div key={day} className="flex items-center mb-0.5">
                <div className="w-12 text-xs text-slate-500 text-right pr-2 shrink-0">{day}</div>
                {HOURS.map(hour => {
                  const cell = lookup.get(`${di}-${hour}`);
                  const roas = cell?.roas ?? 0;
                  return (
                    <div
                      key={hour}
                      className="w-8 h-7 rounded-sm cursor-default transition-opacity hover:opacity-80 mx-px"
                      style={{ backgroundColor: roasBg(roas) }}
                      onMouseEnter={e => cell && setHover({ ...cell, mouseX: e.clientX, mouseY: e.clientY })}
                      onMouseMove={e => hover && setHover(prev => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : null)}
                      onMouseLeave={() => setHover(null)}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {hover && (
        <div
          className="fixed z-50 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm shadow-lg pointer-events-none"
          style={{ left: hover.mouseX + 12, top: hover.mouseY - 64 }}
        >
          <p className="font-medium text-slate-200 mb-1">
            {DAYS[hover.day]} {fmtHour(hover.hour)}
          </p>
          <p className="text-blue-400">ROAS: {hover.roas.toFixed(2)}x</p>
          <p className="text-emerald-400">Revenue: ${hover.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          <p className="text-rose-400">Spend: ${hover.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
        </div>
      )}
    </div>
  );
}
